// De vorm van de gegevens zoals de API ze geeft (contract §6).

export type FieldValue = string | number | string[];

export interface CrmRecord {
  id: string;
  createdTime: string;
  fields: Record<string, FieldValue | undefined>;
}

export const TABLE_KEYS = ["contacts", "companies", "properties", "requests", "contracts", "tasks", "appointments", "tickets", "partners", "interactions", "documents"] as const;
export type TableKey = (typeof TABLE_KEYS)[number];
export type Tables = Record<TableKey, CrmRecord[]>;

export interface Me {
  id: string;
  name: string;
  email: string;
  role: "admin" | "medewerker" | "alleen-lezen";
  permissions: string[];
  isProtected: boolean;
  twoFactorEnabled: boolean;
  mustSetup2fa: boolean;
}

export interface Status {
  live: boolean;
  /** Publieke demo met fictieve gegevens: wachtwoord en 2FA wijzigen kan niet. */
  demo?: boolean;
  calendarEmbedUrl: string;
  driveRootUrl: string;
  jotform: { tenantIntake: string; ownerIntake: string; documentUpload: string; ticketReport: string };
  tables: Record<TableKey, string>;
}

export interface Invoice {
  id: string;
  direction: "incoming" | "outgoing";
  number: string;
  invoiceDate: string;
  dueDate: string | null;
  amount: number;
  status: "open" | "paid" | "cancelled";
  overdue: boolean;
  paidAt: string | null;
  description: string | null;
  propertyId: string | null;
  companyId: string | null;
  contractId: string | null;
  fileName: string;
  fileUrl: string;
  uploadedBy: { id: string; name: string };
  createdAt: string;
}

export interface ActionContext {
  propertyId: string | null;
  companyId: string | null;
  requestId: string | null;
  contractId: string | null;
  ticketId: string | null;
  taskId: string | null;
}

export interface ActionResult {
  action: string;
  summary: string;
  context: ActionContext;
  records: { table: TableKey; operation: "create" | "update"; record: CrmRecord }[];
  extra: { matches?: { id: string; address: string; score: number; reasons: string[] }[]; formUrl?: string; uploadUrl?: string; uploadLinks?: { item: string; url: string }[] };
  jobs?: { type: string; status: "queued" }[];
}

/** Een account in Gebruikers (§6.5). */
export interface User {
  id: string;
  name: string;
  email: string;
  role: Me["role"];
  permissions: string[];
  overrides: { granted: string[]; revoked: string[] };
  status: "active" | "invited" | "deactivated";
  isProtected: boolean;
  twoFactorEnabled: boolean;
  lastLoginAt: string | null;
  activeSessions: number;
  createdAt: string;
  inviteExpiresAt: string | null;
}

export interface Page<T> {
  items: T[];
  nextCursor: string | null;
}

/** Regel in de auditlog (§6.5). */
export interface AuditEntry {
  id: number;
  at: string;
  actor: { id: string; name: string } | null;
  action: string;
  target: { id: string; name: string } | null;
  details: Record<string, unknown>;
}

/** Wijziging van één CRM-record (§6.4b). */
export interface HistoryEntry {
  id: number;
  at: string;
  actor: { id: string; name: string } | null;
  source: "action" | "edit" | "system";
  action: string | null;
  operation: "create" | "update";
  changes: Record<string, { from?: FieldValue | null; to: FieldValue | null }>;
}

/** Bestanden in de Drive-map van een pand of bedrijf (§6.6). */
export interface DriveFiles {
  folder: { driveFolderId: string; url: string } | null;
  items: { driveFileId: string; name: string; mimeType: string; modifiedAt: string | null; url: string }[];
  syncedAt: string | null;
}

/** Een eigen bord in het menu (§6.9). */
export interface BoardSummary {
  id: string;
  name: string;
  icon: string;
  position: number;
  archived: boolean;
  cardCount: number;
}

export interface BoardColumn {
  id: string;
  name: string;
  position: number;
  archived: boolean;
}

/** Een kaart op een eigen bord (§6.9). */
export interface BoardCard {
  id: string;
  boardId: string;
  columnId: string;
  title: string;
  description: string | null;
  assignee: { id: string; name: string } | null;
  dueDate: string | null;
  propertyId: string | null;
  companyId: string | null;
  contactId: string | null;
  position: number;
  archived: boolean;
  createdBy: { id: string; name: string } | null;
  createdAt: string;
  updatedAt: string;
}

export interface Board extends BoardSummary {
  createdAt: string;
  columns: BoardColumn[];
  cards: BoardCard[];
}

/** Kaart in het blok "Op borden" van een dossier: met bord en kolom erbij. */
export interface LinkedCard extends BoardCard {
  board: { id: string; name: string; icon: string };
  column: { id: string; name: string };
}

export interface Person {
  id: string;
  name: string;
}
